import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface LinkedPost {
  id: string;
  content: string;
  createdAt: string;
  profilePicUrl?: string | null;
  hidden?: boolean;
  twitter: { id: string; url: string } | null;
  bluesky: { uri: string; cid: string; url: string } | null;
  farcaster: { hash: string; url: string } | null;
}

export interface PublishedPost {
  id: string;
  content: string;
  createdAt: string;
  hidden?: boolean;
  profilePicUrl?: string | null;
  imageUrls?: string[];
  twitter: { id: string; url: string } | null;
  bluesky: { uri: string; cid: string; url: string } | null;
  farcaster: { hash: string; url: string } | null;
  replyTo: LinkedPost | null;
  quoteOf: LinkedPost | null;
  threadRootId?: string;
  threadLatestActivity?: string;
}

export interface ThreadedPost extends PublishedPost {
  replies: ThreadedPost[];
  depth: number;
}

export interface Thread {
  root: ThreadedPost;
  latestActivity: string;
}

export interface DeletePostResult {
  success: boolean;
  action: 'deleted' | 'hidden';
  hidden: boolean;
  deleted: Record<string, boolean>;
  errors: Record<string, string>;
}

interface UsePublishedPostsOptions {
  limit?: number;
  showHidden?: boolean;
}

function getPostsTestModeFromLocation(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('postsTest') === 'true';
  } catch {
    return false;
  }
}

const POSTS_TEST_MODE = process.env.NODE_ENV !== 'production' && getPostsTestModeFromLocation();

function makeMockImageDataUrl(label: string, startColor: string, endColor: string): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" role="img" aria-label="${label}">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${startColor}" />
          <stop offset="100%" stop-color="${endColor}" />
        </linearGradient>
      </defs>
      <rect width="400" height="300" fill="url(#g)" rx="24" />
      <circle cx="94" cy="96" r="42" fill="rgba(255,255,255,0.18)" />
      <path d="M40 240L138 150L214 214L270 174L360 240Z" fill="rgba(255,255,255,0.24)" />
      <text x="32" y="270" fill="white" font-family="Arial, sans-serif" font-size="28" font-weight="700">${label}</text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const TEST_IMAGE_URLS = {
  backyard: makeMockImageDataUrl('Backyard Build', '#6f8f72', '#d6b86a'),
  night: makeMockImageDataUrl('Night Progress', '#2d4059', '#ea5455'),
  launch: makeMockImageDataUrl('Feature Launch', '#355c7d', '#6c5b7b'),
};

// Build threaded structure from flat posts
function buildThreads(posts: PublishedPost[]): Thread[] {
  if (!posts || posts.length === 0) return [];

  // Create a map of posts by ID
  const postsById: Record<string, PublishedPost> = {};
  posts.forEach((post) => {
    postsById[post.id] = post;
  });

  // Group posts by thread root
  const threadGroups: Record<string, PublishedPost[]> = {};
  posts.forEach((post) => {
    const rootId = post.threadRootId || post.id;
    if (!threadGroups[rootId]) {
      threadGroups[rootId] = [];
    }
    threadGroups[rootId].push(post);
  });

  // Build nested structure for each thread
  const threads: Thread[] = [];

  Object.entries(threadGroups).forEach(([rootId, threadPosts]) => {
    // Find the root post (or create a placeholder if we don't have it)
    const rootPost = postsById[rootId];

    // Build a map of children for each post in this thread
    // Only consider replies, not quotes (quotes should be top-level)
    const childrenMap: Record<string, PublishedPost[]> = {};
    threadPosts.forEach((post) => {
      if (post.replyTo?.id && !post.quoteOf?.id) {
        if (!childrenMap[post.replyTo.id]) {
          childrenMap[post.replyTo.id] = [];
        }
        childrenMap[post.replyTo.id].push(post);
      }
    });

    // Sort children by createdAt ascending (oldest first within thread)
    Object.values(childrenMap).forEach((children) => {
      children.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    });

    // Recursively build the threaded post structure
    const buildThreadedPost = (post: PublishedPost, depth: number): ThreadedPost => {
      const children = childrenMap[post.id] || [];
      return {
        ...post,
        depth: Math.min(depth, 4), // Cap visual depth at 4
        replies: children.map((child) => buildThreadedPost(child, depth + 1)),
      };
    };

    if (rootPost) {
      // We have the root post
      const threadedRoot = buildThreadedPost(rootPost, 0);
      threads.push({
        root: threadedRoot,
        latestActivity: rootPost.threadLatestActivity || rootPost.createdAt,
      });
    } else {
      // Root post not in our dataset
      // Find posts that don't have their parent in our dataset
      const orphans = threadPosts.filter(
        (post) => !post.replyTo?.id || !postsById[post.replyTo.id]
      );

      // Build from each orphan as a sub-root
      orphans.forEach((orphan) => {
        const threadedOrphan = buildThreadedPost(orphan, 0);
        threads.push({
          root: threadedOrphan,
          latestActivity: orphan.threadLatestActivity || orphan.createdAt,
        });
      });
    }
  });

  // Sort threads by latest activity (newest first)
  threads.sort((a, b) =>
    new Date(b.latestActivity).getTime() - new Date(a.latestActivity).getTime()
  );

  return threads;
}

// Mock data for testing the feed UI without real posts
function getTestPosts(): PublishedPost[] {
  const now = new Date();
  const hoursAgo = (h: number) => new Date(now.getTime() - h * 3600000).toISOString();
  const daysAgo = (d: number) => new Date(now.getTime() - d * 86400000).toISOString();

  const rootId = 'test-root-1';

  return [
    {
      id: rootId,
      content: 'Put together over the weekend. The kids love it, the dog is confused by it, and I am mass texting everyone to come over and see it.',
      createdAt: daysAgo(8),
      profilePicUrl: null,
      imageUrls: [TEST_IMAGE_URLS.backyard, TEST_IMAGE_URLS.night],
      twitter: { id: 'tw-001', url: 'https://x.com/test/status/001' },
      bluesky: { uri: 'at://did:plc:test/app.bsky.feed.post/001', cid: 'bafytest001', url: 'https://bsky.app/profile/test.bsky.social/post/001' },
      farcaster: { hash: '0xtest001', url: 'https://warpcast.com/test/0xtest001' },
      replyTo: null,
      quoteOf: null,
      threadRootId: rootId,
    },
    {
      id: 'test-reply-1',
      content: 'I made a huge strategic error building this from the inside out. Help, I\'m starving and my family went to Pizza Hut without me.',
      createdAt: daysAgo(7),
      profilePicUrl: null,
      twitter: { id: 'tw-002', url: 'https://x.com/test/status/002' },
      bluesky: null,
      farcaster: { hash: '0xtest002', url: 'https://warpcast.com/test/0xtest002' },
      replyTo: { id: rootId, content: 'Put together over the weekend.', createdAt: daysAgo(8), twitter: null, bluesky: null, farcaster: null },
      quoteOf: null,
      threadRootId: rootId,
    },
    {
      id: 'test-reply-2',
      content: 'The thing does look incredible, but next time I am starting with the outer frame and budgeting for emergency tacos.',
      createdAt: daysAgo(6),
      profilePicUrl: null,
      twitter: null,
      bluesky: { uri: 'at://did:plc:test/app.bsky.feed.post/006', cid: 'bafytest006', url: 'https://bsky.app/profile/test.bsky.social/post/006' },
      farcaster: null,
      replyTo: { id: rootId, content: 'Put together over the weekend.', createdAt: daysAgo(8), twitter: null, bluesky: null, farcaster: null },
      quoteOf: null,
      threadRootId: rootId,
    },
    {
      id: 'test-reply-3',
      content: 'Update: structure is standing, morale is improving, and I finally got fed. We may survive this project after all.',
      createdAt: daysAgo(5),
      profilePicUrl: null,
      twitter: { id: 'tw-007', url: 'https://x.com/test/status/007' },
      bluesky: null,
      farcaster: { hash: '0xtest007', url: 'https://warpcast.com/test/0xtest007' },
      replyTo: { id: rootId, content: 'Put together over the weekend.', createdAt: daysAgo(8), twitter: null, bluesky: null, farcaster: null },
      quoteOf: null,
      threadRootId: rootId,
    },
    {
      id: 'test-post-2',
      content: 'Just shipped a new feature that lets you crosspost to all three platforms at once. Feels good to finally close that issue.',
      createdAt: hoursAgo(2),
      profilePicUrl: null,
      imageUrls: [TEST_IMAGE_URLS.launch],
      twitter: { id: 'tw-003', url: 'https://x.com/test/status/003' },
      bluesky: { uri: 'at://did:plc:test/app.bsky.feed.post/003', cid: 'bafytest003', url: 'https://bsky.app/profile/test.bsky.social/post/003' },
      farcaster: null,
      replyTo: null,
      quoteOf: null,
    },
    {
      id: 'test-post-3',
      content: 'Hidden example post for delete/retry testing.',
      createdAt: hoursAgo(12),
      hidden: true,
      profilePicUrl: null,
      twitter: null,
      bluesky: null,
      farcaster: { hash: '0xtest004', url: 'https://warpcast.com/test/0xtest004' },
      replyTo: null,
      quoteOf: null,
    },
    {
      id: 'test-quote-1',
      content: 'This is exactly what I was talking about last week. The future of social is multi-platform.',
      createdAt: daysAgo(1),
      profilePicUrl: null,
      twitter: { id: 'tw-005', url: 'https://x.com/test/status/005' },
      bluesky: { uri: 'at://did:plc:test/app.bsky.feed.post/005', cid: 'bafytest005', url: 'https://bsky.app/profile/test.bsky.social/post/005' },
      farcaster: { hash: '0xtest005', url: 'https://warpcast.com/test/0xtest005' },
      replyTo: null,
      quoteOf: { id: 'test-post-2', content: 'Just shipped a new feature...', createdAt: hoursAgo(2), twitter: null, bluesky: null, farcaster: null },
    },
  ];
}

export function usePublishedPosts(userId: string | undefined, options: UsePublishedPostsOptions = {}) {
  const { limit = 20, showHidden = false } = options;
  const [posts, setPosts] = useState<PublishedPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const testMode = POSTS_TEST_MODE;
  const postsRef = useRef(posts);
  postsRef.current = posts;
  // Bumped on every refetch/userId change so stale in-flight loadMore responses are discarded.
  const requestIdRef = useRef(0);
  const threads = useMemo(() => buildThreads(posts), [posts]);

  const buildApiUrl = useCallback((path: string) => {
    const base = process.env.REACT_APP_API_URL;
    const isLocalHost = /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+)$/.test(window.location.hostname);
    if (isLocalHost) {
      return `/api${path}`;
    }
    return base ? `${base.replace(/\/$/, '')}/api${path}` : `/api${path}`;
  }, []);

  const fetchPage = useCallback(async (offset: number): Promise<{ posts: PublishedPost[]; hasMore: boolean }> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return { posts: [], hasMore: false };
    }

    const url = buildApiUrl(
      `/posts?userId=${userId}&limit=${limit}&offset=${offset}&showHidden=${showHidden ? 'true' : 'false'}`
    );
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch posts: ${response.status}`);
    }

    const data = await response.json();
    return { posts: data.posts || [], hasMore: !!data.hasMore };
  }, [userId, limit, buildApiUrl, showHidden]);

  const refetch = useCallback(async () => {
    // Invalidate any in-flight loadMore before we reset state.
    const currentRequestId = ++requestIdRef.current;

    // In test mode, use mock data regardless of auth state
    if (testMode) {
      const testPosts = getTestPosts().filter((post) => showHidden || !post.hidden);
      setPosts(testPosts);
      setHasMore(false);
      return;
    }

    if (!userId) {
      setPosts([]);
      setHasMore(false);
      return;
    }

    setLoading(true);
    setError(null);
    setLoadMoreError(null);

    try {
      const result = await fetchPage(0);
      if (requestIdRef.current !== currentRequestId) return;
      setPosts(result.posts);
      setHasMore(result.hasMore);
    } catch (e) {
      if (requestIdRef.current !== currentRequestId) return;
      const message = e instanceof Error ? e.message : 'Failed to fetch posts';
      setError(message);
      setPosts([]);
      setHasMore(false);
    } finally {
      if (requestIdRef.current === currentRequestId) {
        setLoading(false);
      }
    }
  }, [userId, showHidden, testMode, fetchPage]);

  const loadMore = useCallback(async () => {
    if (testMode || !userId) return;
    if (loadingMore || loading || !hasMore) return;

    const currentRequestId = requestIdRef.current;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const result = await fetchPage(postsRef.current.length);
      if (requestIdRef.current !== currentRequestId) return;
      setPosts((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const newOnes = result.posts.filter((p) => !existingIds.has(p.id));
        return [...prev, ...newOnes];
      });
      setHasMore(result.hasMore);
    } catch (e) {
      if (requestIdRef.current !== currentRequestId) return;
      const message = e instanceof Error ? e.message : 'Failed to load more posts';
      setLoadMoreError(message);
    } finally {
      if (requestIdRef.current === currentRequestId) {
        setLoadingMore(false);
      }
    }
  }, [userId, testMode, loading, loadingMore, hasMore, fetchPage]);

  // Fetch posts when userId changes (or in test mode, on mount)
  useEffect(() => {
    if (testMode || userId) {
      refetch();
    } else {
      setPosts([]);
      setHasMore(false);
    }
  }, [userId, testMode, refetch]);

  const deletePost = useCallback(async (postId: string): Promise<DeletePostResult> => {
    if (testMode) {
      const target = postsRef.current.find((post) => post.id === postId);
      const isRetry = !!target?.hidden;

      if (isRetry) {
        // Retry: simulate successful full deletion
        setPosts((prev) => prev.filter((post) => post.id !== postId));
        return {
          success: true,
          action: 'deleted',
          hidden: false,
          deleted: { farcaster: true },
          errors: {},
        };
      }

      setPosts((prev) => {
        return showHidden
          ? prev.map((post) => post.id === postId ? { ...post, hidden: true } : post)
          : prev.filter((post) => post.id !== postId);
      });

      return {
        success: true,
        action: 'hidden',
        hidden: true,
        deleted: {},
        errors: {},
      };
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Missing auth session');
    }

    const response = await fetch(buildApiUrl(`/posts/${postId}`), {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete post: ${response.status}`);
    }

    const result = await response.json() as DeletePostResult;
    setPosts((prev) => {
      if (result.action === 'deleted' || !showHidden) {
        return prev.filter((post) => post.id !== postId);
      }

      return prev.map((post) => post.id === postId ? { ...post, hidden: true } : post);
    });
    return result;
  }, [buildApiUrl, showHidden, testMode]);

  return {
    posts,
    threads,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMoreError,
    refetch,
    loadMore,
    deletePost,
    testMode,
  };
}

// Helper to format relative time
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function isPostsTestModeEnabled(): boolean {
  return POSTS_TEST_MODE;
}
